import { t } from '@lingui/macro';
import { View, UserView, CheckIcon, CancelIcon, ButtonView, BaseSuggestionThreadView, } from 'pleditor';
import { updateView } from './utils';
export class CustomSuggestionThreadView extends BaseSuggestionThreadView {
    constructor(locale, suggestion, currentUser, config) {
        super(locale, suggestion, currentUser, config);
        this.set({
            type: '',
            description: '',
        });
        this.config = config;
        this.locale = locale;
        this.suggestion = suggestion;
        this.isExternal = process.env.WEB_CLIENT === 'external';
        this.canAccept = !this.isExternal;
        this.canDiscard = currentUser.id === suggestion.creator.id || !this.isExternal;
        this.setupBinds();
        /**
         * User View
         * */
        const userView = new UserView(locale, suggestion.author || currentUser);
        userView.extendTemplate({
            attributes: {
                class: ['ck-suggestion__user', 'ck-annotation__user'],
            },
        });
        this.userView = userView;
        /**
         * Description View
         * */
        const descriptionView = new View();
        descriptionView.setTemplate({
            tag: 'div',
            attributes: {
                class: ['ck-annotation__content-wrapper'],
            },
        });
        this.descriptionView = descriptionView;
        /**
         * Accept Button
         * */
        const acceptButton = new ButtonView();
        acceptButton.set({
            tooltip: true,
            icon: CheckIcon,
            class: 'ck-suggestion--accept',
            label: t({
                comment: 'Pleditor suggestions - Accept suggestion button tooltip',
                message: 'Accept suggestion',
            }),
            isEnabled: this.canAccept,
        });
        acceptButton.on('execute', () => {
            if (this.canAccept) {
                this.fire('acceptSuggestion');
            }
        });
        this.acceptButton = acceptButton;
        /**
         * Discard Button
         * */
        const discardButton = new ButtonView();
        discardButton.set({
            tooltip: true,
            icon: CancelIcon,
            class: 'ck-suggestion--discard',
            label: t({
                comment: 'Pleditor suggestions - Discard suggestion button tooltip',
                message: 'Discard suggestion',
            }),
            isEnabled: this.canDiscard,
        });
        discardButton.on('execute', () => {
            if (this.canDiscard) {
                this.fire('discardSuggestion');
            }
        });
        this.discardButton = discardButton;
        /**
         * Template
         * */
        this.setTemplate(this.getTemplate());
    }
    getTemplate() {
        var _a;
        return {
            tag: 'div',
            attributes: {
                class: [
                    'ck-suggestion-wrapper',
                    this.bindTemplate.if('isActive', 'ck-suggestion-wrapper--active'),
                    this.bindTemplate.to('type', value => `ck-suggestion-${value}`),
                    this.config.disableComments && 'ck-suggestion--disabled-comments',
                ],
                'data-suggestion-id': this.suggestion.id,
                'data-thread-id': (_a = this.suggestion.commentThread) === null || _a === void 0 ? void 0 : _a.id,
                'data-author-id': this.suggestion.author.id,
                // Needed for managing focus after adding a new comment.
                tabindex: -1,
            },
            children: [
                {
                    tag: 'div',
                    attributes: {
                        class: ['ck-suggestion', 'ck-annotation'],
                    },
                    children: [
                        this.userView,
                        {
                            tag: 'div',
                            attributes: {
                                class: ['ck-suggestion__main', 'ck-annotation__main'],
                            },
                            children: [
                                {
                                    tag: 'div',
                                    attributes: {
                                        class: ['ck-suggestion__info', 'ck-annotation__info'],
                                    },
                                    children: [
                                        {
                                            tag: 'span',
                                            attributes: {
                                                class: ['ck-suggestion__info-name', 'ck-annotation__info-name'],
                                            },
                                            children: [
                                                {
                                                    text: this.userView.name,
                                                },
                                            ],
                                        },
                                        {
                                            tag: 'time',
                                            attributes: {
                                                class: ['ck-comment__info-time', 'ck-annotation__info-time'],
                                                datetime: this.bindTemplate.to('authoredAt'),
                                            },
                                            children: [
                                                {
                                                    text: this.bindTemplate.to('authoredAt', value => this.config.formatDateTime(value || '')),
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    tag: 'div',
                                    attributes: {
                                        class: ['ck-suggestion__actions', 'ck-annotation__actions'],
                                    },
                                    children: [this.acceptButton, this.discardButton],
                                },
                                this.descriptionView,
                            ],
                        },
                    ],
                },
            ],
        };
    }
    setupBinds() {
        // We update type whenever descriptionParts is updated
        this.bind('type').to(this, 'descriptionParts', parts => parts && parts[0] ? parts[0].type : '');
        // We update description whenever descriptionParts is updated
        this.bind('description').to(this, 'descriptionParts', parts => {
            if (parts.length === 0) {
                return '';
            }
            return parts[0].content;
        });
    }
    render() {
        super.render();
        this.on('change:description', (_eventInfo, _name, description) => {
            updateView(this.descriptionView, description);
        });
    }
    // eslint-disable-next-line class-methods-use-this
    getExternalSuggestionNote() {
        return '';
    }
    // eslint-disable-next-line class-methods-use-this
    getUserViewNotificationText() {
        return '';
    }
}
